import { ColDef, SuppressKeyboardEventParams } from 'ag-grid-community'
import { useEffect, useMemo, useRef } from 'react'
import { ProgressReportTableContent } from '../components/table/ProgressReportTable'
import { FontWeight } from '../../../../styles/commonStyles'
import { EntitySearchCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { ReportStatusCellRenderer } from '../components/cell/ReportStatusCellRenderer'
import { CommentCellRenderer } from '../components/cell/CommentCellRenderer'
import { ProgressRateDiffCellRenderer } from '../components/cell/ProgressRateDiffCellRenderer'
import { ProgressReportQualitativeContent } from '../../../../domain/entity/version-report/VersionReportVersionEntity'

type ColumnDefProps = {
  editing: boolean
  updateTableContent: (
    content: ProgressReportQualitativeContent,
    rowIndex: number
  ) => void
}

export const useProcessReportColumnDef = ({
  editing,
  updateTableContent,
}: ColumnDefProps) => {
  const updateTableContentRef = useRef(updateTableContent)
  useEffect(() => {
    // for avoiding re-rendering by updateTableContent change
    updateTableContentRef.current = updateTableContent
  }, [updateTableContent])

  const columns = useMemo<ColDef<ProgressReportTableContent>[]>(
    () => [
      {
        headerName: '名称',
        field: 'name',
        pinned: 'left',
        lockPinned: !editing,
        wrapText: true,
        width: 300,
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-start',
        },
      },
      {
        headerName: 'チーム',
        field: 'team',
        pinned: 'left',
        lockPinned: !editing,
        width: 110,
        cellRenderer: EntitySearchCellRenderer,
      },
      {
        headerName: 'ステータス',
        field: 'status',
        pinned: 'left',
        lockPinned: !editing,
        width: 90,
        cellRenderer: ReportStatusCellRenderer,
        cellRendererParams: {
          editing,
          updateTableContent: (
            content: ProgressReportTableContent,
            rowIndex: number
          ) => updateTableContentRef.current(content, rowIndex),
        },
      },
      {
        headerName: '進捗率予実乖離',
        field: 'progressRateDiff',
        pinned: 'left',
        lockPinned: !editing,
        width: 110,
        cellClass: 'ag-numeric-cell',
        cellRenderer: ProgressRateDiffCellRenderer,
      },
      {
        headerName: '工程ステータス',
        field: 'wbsItemStatus',
        pinned: 'left',
        lockPinned: !editing,
        width: 110,
      },
      {
        headerName: '予定開始日',
        field: 'scheduledStartDate',
        pinned: 'left',
        lockPinned: !editing,
        width: 110,
      },
      {
        headerName: '予定終了日',
        field: 'scheduledEndDate',
        pinned: 'left',
        lockPinned: !editing,
        width: 110,
      },
      {
        headerName: '子の最大予定終了日',
        field: 'maxChildScheduledEndDate',
        pinned: 'left',
        lockPinned: !editing,
        width: 140,
      },
      {
        headerName: '予定進捗率',
        field: 'scheduledProgressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '進捗率',
        field: 'progressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '総数',
        field: 'total',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '予定',
        field: 'planned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '完了',
        field: 'completed',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '遅延',
        field: 'delay',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '残',
        field: 'remaining',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '先行',
        field: 'preceding',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '未計画',
        field: 'unplanned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '定性コメント',
        field: 'comment',
        width: 600,
        editable: editing,
        lockPinned: !editing,
        cellRenderer: CommentCellRenderer,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          rows: 15,
          cols: 70,
        },
        suppressKeyboardEvent: suppressEnterKeyWithoutShift,
        onCellValueChanged: params => {
          const rowIndex = params.node?.rowIndex!
          const content: ProgressReportQualitativeContent = {
            uuid: params.data.uuid,
            status: params.data.status,
            comment: params.data.comment,
          }
          updateTableContentRef.current(content, rowIndex)
        },
      },
    ],
    [editing]
  )
  return columns
}

export const useTeamReportColumnDef = ({
  editing,
  updateTableContent,
}: ColumnDefProps) => {
  const updateTableContentRef = useRef(updateTableContent)
  useEffect(() => {
    // for avoiding re-rendering by updateTableContent change
    updateTableContentRef.current = updateTableContent
  }, [updateTableContent])

  const columns = useMemo<ColDef<ProgressReportTableContent>[]>(
    () => [
      {
        headerName: '名称',
        field: 'name',
        pinned: 'left',
        lockPinned: !editing,
        width: 300,
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-start',
        },
      },
      {
        headerName: 'ステータス',
        field: 'status',
        pinned: 'left',
        lockPinned: !editing,
        width: 90,
        cellRenderer: ReportStatusCellRenderer,
        cellRendererParams: {
          editing,
          updateTableContent: (
            content: ProgressReportTableContent,
            rowIndex: number
          ) => updateTableContentRef.current(content, rowIndex),
        },
      },
      {
        headerName: '進捗率予実乖離',
        field: 'progressRateDiff',
        pinned: 'left',
        lockPinned: !editing,
        width: 110,
        cellClass: 'ag-numeric-cell',
        cellRenderer: ProgressRateDiffCellRenderer,
      },
      {
        headerName: '予定進捗率',
        field: 'scheduledProgressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '進捗率',
        field: 'progressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '総数',
        field: 'total',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '予定',
        field: 'planned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '完了',
        field: 'completed',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '遅延',
        field: 'delay',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '残',
        field: 'remaining',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '先行',
        field: 'preceding',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '未計画',
        field: 'unplanned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-end',
        },
        lockPinned: !editing,
      },
      {
        headerName: '定性コメント',
        field: 'comment',
        width: 600,
        editable: editing,
        lockPinned: !editing,
        cellRenderer: CommentCellRenderer,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          rows: 15,
          cols: 70,
        },
        suppressKeyboardEvent: suppressEnterKeyWithoutShift,
        onCellValueChanged: params => {
          const rowIndex = params.node?.rowIndex!
          const content: ProgressReportQualitativeContent = {
            uuid: params.data.uuid,
            status: params.data.status,
            comment: params.data.comment,
          }
          updateTableContentRef.current(content, rowIndex)
        },
      },
    ],
    [editing]
  )
  return columns
}

const suppressEnterKeyWithoutShift = (
  params: SuppressKeyboardEventParams<ProgressReportTableContent>
) => {
  return params.editing && params.event.key === 'Enter' && params.event.shiftKey
}
